import { useEffect, useRef } from "react";
import { blocksRefAtom, selectionAtom } from "../../jotai/atoms";
import { ImageSearchBlock, AnyBlock } from "../../types";
import { createBlock } from "../../blocks/createBlock";
import { pixelToGridPosition } from "../../utils/gridPosition";
import { DEFAULT_SPACE_SIZE } from "../../consts";
import { useBlocks } from "../../liveblocks/maps";
import { useAtom } from "jotai";
import { Selection, Position } from "../../types";
import { useSendAnalyticsEvent } from "../../analytics/events";

type PastePosition = Record<string, Position>;

function useCopyPasteBlocks() {
  const [selection, setSelection] = useAtom(selectionAtom);
  const { upsertBlock, deleteBlock } = useBlocks();
  const [blocksRef] = useAtom(blocksRefAtom);
  const pastePositionRef = useRef<PastePosition>({});
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  useEffect(() => {
    const SPROUT_FORMAT = "application/sprout";

    const copySelectedBlocks = (e: ClipboardEvent) => {
      // clear paste position, we use paste position to check if we need to offset pasted blocks
      pastePositionRef.current = {};

      if (blocksRef.current) {
        const toCopy: AnyBlock[] = [];
        for (const id of Object.keys(selection)) {
          if (blocksRef.current[id]) {
            const block = blocksRef.current[id];
            pastePositionRef.current[id] = { ...block.position };
            toCopy.push(block);
          }
        }
        const json = JSON.stringify(toCopy);
        e.clipboardData?.setData(SPROUT_FORMAT, json);
      }

      // need to prevent default to keep clipboardData set
      e.preventDefault();
    };

    function onCopy(e: ClipboardEvent): void {
      // if textarea is focused do not copy block
      if (
        document.activeElement &&
        document.activeElement.tagName.toLowerCase() === "textarea"
      )
        return;
      copySelectedBlocks(e);
    }

    function onCut(e: ClipboardEvent): void {
      // if textarea is focused do not cut block
      if (
        document.activeElement &&
        document.activeElement.tagName.toLowerCase() === "textarea"
      )
        return;
      copySelectedBlocks(e);
      for (const id of Object.keys(selection)) {
        deleteBlock(id);
      }
    }

    function onPaste(e: ClipboardEvent): void {
      // check clipboard for valid data
      const data = e.clipboardData?.getData(SPROUT_FORMAT);
      if (data === undefined || data.length === 0) return;

      const pastedBlocks = (JSON.parse(data) as AnyBlock[]).sort(
        (a: AnyBlock, b: AnyBlock) => {
          return a.updatedAt - b.updatedAt;
        }
      );

      // use paste position for block position if we have it
      for (let i = 0; i < pastedBlocks.length; i++) {
        const pastedBlock = pastedBlocks[i];
        if (pastePositionRef.current[pastedBlock.id]) {
          pastedBlock.position.x = pastePositionRef.current[pastedBlock.id].x;
          pastedBlock.position.y = pastePositionRef.current[pastedBlock.id].y;
        }
      }

      // get viewport info
      const topLeft = pixelToGridPosition({
        x: window.scrollX,
        y: window.scrollY,
      });
      const bottomRight = pixelToGridPosition({
        x: window.scrollX + window.innerWidth,
        y: window.scrollY + window.innerHeight,
      });
      const viewportBox = {
        minX: topLeft.x,
        minY: topLeft.y,
        maxX: bottomRight.x,
        maxY: bottomRight.y,
      };

      // get position of pasted blocks to compare with viewport
      let pasteBox = {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
      };
      for (let i = 0; i < pastedBlocks.length; i++) {
        const pastedBlock = pastedBlocks[i];
        pasteBox = {
          minX: Math.min(pasteBox.minX, pastedBlock.position.x),
          minY: Math.min(pasteBox.minY, pastedBlock.position.y),
          maxX: Math.max(
            pasteBox.maxX,
            pastedBlock.position.x + pastedBlock.size.width
          ),
          maxY: Math.max(
            pasteBox.maxY,
            pastedBlock.position.y + pastedBlock.size.height
          ),
        };
      }

      // handle case where pasted blocks are outside of viewport
      // recenter around viewport center
      // handle x and y separately so we only recenter what we need to
      if (
        pasteBox.maxX < viewportBox.minX ||
        pasteBox.minX > viewportBox.maxX
      ) {
        const pasteBoxCenterX = (pasteBox.minX + pasteBox.maxX) / 2;
        const viewportCenterX = (viewportBox.minX + viewportBox.maxX) / 2;
        const deltaX = viewportCenterX - pasteBoxCenterX;
        for (let i = 0; i < pastedBlocks.length; i++) {
          const pastedBlock = pastedBlocks[i];
          pastedBlock.position.x += deltaX;
        }
      }
      if (
        pasteBox.maxY < viewportBox.minY ||
        pasteBox.minY > viewportBox.maxY
      ) {
        const pasteBoxCenterY = (pasteBox.minY + pasteBox.maxY) / 2;
        const viewportCenterY = (viewportBox.minY + viewportBox.maxY) / 2;
        const deltaY = viewportCenterY - pasteBoxCenterY;
        for (let i = 0; i < pastedBlocks.length; i++) {
          const pastedBlock = pastedBlocks[i];
          pastedBlock.position.y += deltaY;
        }
      }

      const offset = { x: 1, y: -1 };
      const newSelections: Selection = {};

      for (let i = 0; i < pastedBlocks.length; i++) {
        const pastedBlock = pastedBlocks[i];

        // offset pasted blocks if they are the same as paste position
        if (pastePositionRef.current) {
          const pastePosition = pastePositionRef.current[pastedBlock.id];
          if (pastePosition) {
            if (
              pastePosition.x === pastedBlock.position.x &&
              pastePosition.y === pastedBlock.position.y
            ) {
              pastedBlock.position.x += offset.x;
              pastedBlock.position.y += offset.y;
            }
          }
        }

        // keep blocks inside canvas
        if (pastedBlock.position.x < 0) pastedBlock.position.x = 0;
        if (pastedBlock.position.y < 0) pastedBlock.position.y = 0;
        if (
          pastedBlock.position.x + pastedBlock.size.width >
          DEFAULT_SPACE_SIZE.width
        )
          pastedBlock.position.x =
            DEFAULT_SPACE_SIZE.width - pastedBlock.size.width;
        if (
          pastedBlock.position.y + pastedBlock.size.height >
          DEFAULT_SPACE_SIZE.height
        )
          pastedBlock.position.y =
            DEFAULT_SPACE_SIZE.height - pastedBlock.size.height;

        // update paste position ref with new position
        pastePositionRef.current[pastedBlock.id] = {
          x: pastedBlock.position.x,
          y: pastedBlock.position.y,
        };

        const block = createBlock(
          pastedBlock.type,
          pastedBlock.position,
          pastedBlock.size,
          pastedBlock.value
        );

        // ImageSearchBlock has extra properties, any future blocks that have extra
        // properties we'll also have to special case
        // Maybe there is a more sustainable way to deal with this using block types?
        if (pastedBlock.type === "imageSearch") {
          const imageSearchBlock = block as ImageSearchBlock;
          imageSearchBlock.text = pastedBlock.text;
          imageSearchBlock.searchType = pastedBlock.searchType;
          imageSearchBlock.offset = pastedBlock.offset;
        }

        if (block) {
          if (pastedBlock.disabled !== undefined) {
            block.disabled = pastedBlock.disabled;
          }

          sendAnalyticsEvent({
            name: "blockCreated",
            type: block.type,
            pasted: true,
          });

          upsertBlock(block);
          // make sure order is preserved
          newSelections[block.id] = Date.now() + i;
        }
      }
      setSelection({ ...newSelections });
    }

    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCut);
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCut);
      document.removeEventListener("paste", onPaste);
    };
  }, [
    blocksRef,
    deleteBlock,
    selection,
    upsertBlock,
    setSelection,
    sendAnalyticsEvent,
  ]);

  return null;
}

export default useCopyPasteBlocks;
